// A modal's backdrop that opens its panel next to the control that opened it, so the mouse doesn't cross the screen
// and back (Mitch, 2026-10-31: "pop-up UI modules should have a relation to where they are clicked"). The panel (the
// layer's one child) goes just below that control, lined up with its right edge when the control is on the right half
// of the screen and its left edge otherwise; above it when there's no room below; kept on screen, and the layer
// scrolls when the panel is taller than the window. Opened by a shortcut (N, Ctrl+K) rather than a click or Enter on a
// control, it's centred as before. Every dialog uses it; closing, Esc and focus stay the dialog's own. It sets
// `data-dialog`, which ListPage, the item panel and the spec editor read to leave Esc to an open dialog.
import { type ComponentProps, useLayoutEffect, useRef, useState } from "react";
import { cx } from "./controls.tsx";

/** How long after a click (or Enter on a control) a dialog that opens still counts as opened by it. */
const OPENED_BY_MS = 2000;
const MARGIN = 8;
const GAP = 6;

let trigger: { rect: DOMRect; at: number } | null = null;

/** The control a dialog opening now was opened from, if one was just clicked or pressed. */
function recentTrigger(): DOMRect | null {
  return trigger && performance.now() - trigger.at < OPENED_BY_MS ? trigger.rect : null;
}

if (typeof document !== "undefined") {
  // Captured before React handles the event, and as a rect, since the control may be gone once the dialog opens (a
  // menu item, a row action).
  document.addEventListener(
    "pointerdown",
    (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const control = target?.closest("button, a, [role=button], [role=menuitem], [role=option], label, tr") ?? target;
      trigger = control ? { rect: control.getBoundingClientRect(), at: performance.now() } : null;
    },
    true,
  );
  document.addEventListener(
    "keydown",
    (e) => {
      const el = document.activeElement;
      // Enter or Space on a control opens it from there; any other key (N, Ctrl+K) is a shortcut: centred
      if ((e.key === "Enter" || e.key === " ") && el instanceof HTMLElement && el !== document.body)
        trigger = { rect: el.getBoundingClientRect(), at: performance.now() };
      else if (!["Shift", "Control", "Alt", "Meta"].includes(e.key)) trigger = null;
    },
    true,
  );
}

type Side = "below" | "above";

/** Where the panel goes beside `at` (viewport pixels): below it, else above it, else as low as it fits. Given the side
 * it was put on, it stays there as its content grows (a pasted preview) and slides just far enough to stay on screen,
 * rather than jumping across the control. */
function placeBeside(at: DOMRect, w: number, h: number, side?: Side): { left: number; top: number; side: Side } {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const left = at.left + at.width / 2 > width / 2 ? at.right - w : at.left;
  const below = at.bottom + GAP;
  const above = at.top - GAP - h;
  const s = side ?? (below + h <= height - MARGIN || above < MARGIN ? "below" : "above");
  const top = s === "below" ? Math.min(below, height - MARGIN - h) : above;
  return { left: Math.min(Math.max(MARGIN, left), Math.max(MARGIN, width - MARGIN - w)), top: Math.max(MARGIN, top), side: s };
}

export function DialogLayer({ className, children, z = 40, ...props }: ComponentProps<"div"> & { z?: 40 | 50 }) {
  const layer = useRef<HTMLDivElement>(null);
  const [at] = useState(recentTrigger);
  useLayoutEffect(() => {
    const panel = layer.current?.firstElementChild;
    if (!at || !(panel instanceof HTMLElement)) return;
    let side: Side | undefined;
    const place = () => {
      const { left, top, side: placed } = placeBeside(at, panel.offsetWidth, panel.offsetHeight, side);
      side = placed;
      Object.assign(panel.style, { position: "absolute", left: `${left}px`, top: `${top}px`, marginBottom: `${MARGIN}px` });
    };
    place();
    // Its content can change size (a loaded list, a step): keep it beside the control and on screen
    const watch = new ResizeObserver(place);
    watch.observe(panel);
    window.addEventListener("resize", place);
    return () => {
      watch.disconnect();
      window.removeEventListener("resize", place);
    };
  }, [at]);
  return (
    <div
      ref={layer}
      data-dialog
      className={cx("fixed inset-0 overflow-y-auto bg-black/30", z === 50 ? "z-50" : "z-40", !at && "flex items-start justify-center py-[10vh]", className)}
      {...props}
    >
      {children}
    </div>
  );
}
